/** we pass in default styling via the theme, but if we want to render components through the async-rootcomponent and still have the block specific style we can now do this with this context */

import { tryParseJSONObject } from '@fox/atoms';
import React, { createContext, useContext } from 'react';
import styled from 'styled-components';

//we need to add all other attributes here as well
export type Style = {
	textAlignment: string;
	backgroundColor: string;
	font: string;
	fontColor: string;
	fontSize: string;
	fontTextShadow: boolean;
	headerFont: string;
	headerFontColor: string;
	headerTextShadow: boolean;
	headerSubtextFont: string;
	headerSubtextFontColor: string;
	borderStyle: string;
	borderWidth: string;
	borderColour: string;
	borderRadius: string;
	boxShadowStyle: string;
	linkColour: string;
	headerLinkColour: string;
	subtextLinkColour: string;
	linkHoverColour: string;
	linkHoverUnderline: boolean;
	cardBackgroundColour: string;
	cardBorderColour: string;
	cardBorderRadius: string;
	cardBorderStyle: string;
	cardBorderWidth: string;
	cardBoxShadowStyle: string;
	cardFont: string;
	cardFontColour: string;
	cardHeaderFont: string;
	cardHeaderFontColour: string;
};
export type ComponentStyleContextProps = {
	style: string;
};

export const getBoxShadowValue = value => {
	if (value?.startsWith('box-shadow:')) {
		return value.replace('box-shadow:', '').trim();
	}
	switch (value) {
		case 'faint shadow':
			return 'rgba(149, 157, 165, 0.2) 0px 8px 24px';
		case 'half grey shadow':
			return 'rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px';
		case 'full shadow':
			return 'rgba(0, 0, 0, 0.35) 0px 5px 15px';
		case 'solid frame 1px':
			return 'rgba(0, 0, 0, 0.16) 0px 1px 4px, rgb(51, 51, 51) 0px 0px 0px 3px';
		case 'full shadow 3D':
			return 'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px';
		case 'solid frame 0.5px':
			return 'rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset';
		case 'rainbow frame':
			return 'rgb(85, 91, 255) 0px 0px 0px 3px, rgb(31, 193, 27) 0px 0px 0px 6px, rgb(255, 217, 19) 0px 0px 0px 9px, rgb(255, 156, 85) 0px 0px 0px 12px, rgb(255, 85, 85) 0px 0px 0px 15px';
		case 'half blue-grey blur':
			return 'rgba(136, 165, 191, 0.48) 6px 2px 16px 0px, rgba(255, 255, 255, 0.8) -6px -2px 16px 0px';
		case 'curved shade':
			return 'rgba(0, 0, 0, 0.17) 0px -23px 25px 0px inset, rgba(0, 0, 0, 0.15) 0px -36px 30px 0px inset, rgba(0, 0, 0, 0.1) 0px -79px 40px 0px inset, rgba(0, 0, 0, 0.06) 0px 2px 1px, rgba(0, 0, 0, 0.09) 0px 4px 2px, rgba(0, 0, 0, 0.09) 0px 8px 4px, rgba(0, 0, 0, 0.09) 0px 16px 8px, rgba(0, 0, 0, 0.09) 0px 32px 16px';
		case 'grey solid frame 0.5px':
			return 'rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px';
		case 'none':
			return 'none';
		default:
			return value;
	}
};

export type ComponentStyleContext = Record<StyleMapKeys, React.CSSProperties>;
//we need to do this for all the other attributes as well
type StyleMapKeys =
	| 'textAlignment'
	| 'backgroundColour'
	| 'font'
	| 'fontColour'
	| 'fontSize'
	| 'fontTextShadow'
	| 'headerFont'
	| 'headerFontColour'
	| 'headerFontSize'
	| 'headerTextShadow'
	| 'subtextFont'
	| 'subtextFontColour'
	| 'borderStyle'
	| 'borderWidth'
	| 'borderColour'
	| 'borderRadius'
	| 'boxShadowStyle'
	| 'linkColour'
	| 'linkHoverColour'
	| 'linkHoverUnderline'
	| 'cardBackgroundColour'
	| 'cardBorderColour'
	| 'cardBorderRadius'
	| 'cardBorderStyle'
	| 'cardBorderWidth'
	| 'cardBoxShadowStyle'
	| 'cardCustomBoxShadow'
	| 'cardFont'
	| 'cardFontColour'
	| 'cardHeaderFont'
	| 'cardHeaderFontColour';
const StyleMap: Record<StyleMapKeys, (value) => Record<string, unknown> | undefined> = {
	textAlignment: value => {
		if (value) return { textAlign: value };
	},
	backgroundColour: value => {
		if (value) return { backgroundColor: value };
	},
	font: (value: string) => {
		if (value) {
			if (value.endsWith('-bold')) {
				return { fontFamily: value, fontWeight: 700 };
			}
			return { fontFamily: value };
		}
	},
	fontColour: value => {
		if (value) return { color: value };
	},
	fontSize: value => {
		if (value !== null && value !== undefined && value !== '') return { fontSize: value };
	},
	fontTextShadow: value => {
		if (value) {
			return { textShadow: 'rgba(0,0,0,.8) 3px 3px 10px' };
		}
	},
	headerFont: (value: string) => {
		if (value) {
			if (value.endsWith('-bold')) {
				return { fontFamily: value, fontWeight: 700 };
			}
			return { fontFamily: value };
		}
	},
	headerFontColour: value => {
		if (value) return { color: value };
	},
	headerFontSize: value => {
		if (value !== null && value !== undefined && value !== '') return { fontSize: value };
	},
	headerTextShadow: value => {
		if (value) {
			return { textShadow: 'rgba(0,0,0,.8) 3px 3px 10px' };
		}
	},
	subtextFont: (value: string) => {
		if (value) {
			if (value.endsWith('-bold')) {
				return { fontFamily: value, fontWeight: 700 };
			}
			return { fontFamily: value };
		}
	},
	subtextFontColour: value => {
		if (value) return { color: value };
	},
	borderColour: value => {
		if (value) return { borderColor: value };
	},
	borderRadius: value => {
		if (value === null || value === undefined || value === '') return undefined;
		return { borderRadius: value };
	},
	borderStyle: value => {
		if (value) return { borderStyle: value };
	},
	borderWidth: value => {
		if (value !== null && value !== undefined && value !== '') return { borderWidth: value };
	},
	linkColour: value => {
		if (value) return { color: value };
	},
	linkHoverColour: value => {
		if (value) return { color: value };
	},
	linkHoverUnderline: value => {
		if (value) return { textDecoration: 'underline' };
	},
	boxShadowStyle: value => {
		if (value) return { boxShadow: getBoxShadowValue(value) };
	},
	cardBackgroundColour: value => {
		if (value) return { backgroundColor: value };
	},
	cardBorderColour: value => {
		if (value) return { borderColor: value };
	},
	cardBorderRadius: value => {
		if (value !== null && value !== undefined && value !== '') return { borderRadius: value };
	},
	cardBorderStyle: value => {
		if (value) return { borderStyle: value };
	},
	cardBorderWidth: value => {
		if (value !== null && value !== undefined && value !== '') return { borderWidth: value };
	},
	cardBoxShadowStyle: value => {
		return { boxShadow: getBoxShadowValue(value) };
	},
	cardCustomBoxShadow: value => {
		if (value) return { boxShadow: value };
	},
	cardFont: (value: string) => {
		if (value) {
			if (value.endsWith('-bold')) {
				return { fontFamily: value, fontWeight: 700 };
			}
			return { fontFamily: value };
		}
	},
	cardFontColour: value => {
		if (value) return { color: value };
	},
	cardHeaderFont: (value: string) => {
		if (value) {
			if (value.endsWith('-bold')) {
				return { fontFamily: value, fontWeight: 700 };
			}
			return { fontFamily: value };
		}
	},
	cardHeaderFontColour: value => {
		if (value) return { color: value };
	}
};

export const HoverWrapper = styled.div<{
	$linkColour?: string;
	$hasLinkColour?: boolean;
	$linkHoverColour?: string;
	$linkHoverUnderline?: boolean;
}>`
	--block-link-colour: ${props => props.$linkColour || 'initial'};

	${props =>
		props.$linkColour || props.$hasLinkColour
			? `.link-style,
	.link-style * {
		color: var(--block-link-colour, inherit) !important;
	}`
			: ''}

	.link-style:hover,
	.link-style:hover *,
	.link-style-active,
	.link-style-active * {
		${props => (props.$linkHoverColour ? `color: ${props.$linkHoverColour}!important;` : '')}
		${props => (props.$linkHoverUnderline ? 'text-decoration: underline!important;' : '')}
	}
`;

function generateStyle(style: Style): Record<StyleMapKeys, React.CSSProperties> {
	if (!style) return {} as Record<StyleMapKeys, React.CSSProperties>;
	const styling = Object.entries(style)?.reduce((prev, [key, value]) => {
		const val = StyleMap[key]?.(value);
		if (val) {
			prev = { ...prev, [key]: val };
		}
		return prev;
	}, {} as Record<StyleMapKeys, React.CSSProperties>);
	const linkColours = {
		fontColour: style.linkColour,
		headerFontColour: style.headerLinkColour,
		subtextFontColour: style.subtextLinkColour,
		cardFontColour: style.cardFontColour?.trim() || style.linkColour,
		cardHeaderFontColour: style.cardHeaderFontColour?.trim() || style.headerLinkColour
	};
	for (const [key, colour] of Object.entries(linkColours)) {
		styling[key] = { ...styling[key], '--block-link-colour': colour || 'initial' };
	}
	return styling;
}

const Context = createContext<ComponentStyleContext>(undefined);
Context.displayName = 'ComponentStyleContext';

export function ComponentStyleContextProvider({ children, style }: { style: string; children: React.ReactNode }) {
	const parsedStyle = tryParseJSONObject<Style>(style);
	const styling = generateStyle(parsedStyle);
	const contextValue = {
		...styling
	};

	return (
		<Context.Provider value={contextValue}>
			<HoverWrapper
				$linkColour={parsedStyle?.linkColour}
				$hasLinkColour={Boolean(
					parsedStyle?.headerLinkColour ||
						parsedStyle?.subtextLinkColour ||
						parsedStyle?.cardFontColour?.trim() ||
						parsedStyle?.cardHeaderFontColour?.trim()
				)}
				$linkHoverColour={parsedStyle?.linkHoverColour}
				$linkHoverUnderline={parsedStyle?.linkHoverUnderline}
			>
				{children}
			</HoverWrapper>
		</Context.Provider>
	);
}

export function useComponentStyleContext() {
	const context = useContext(Context);

	if (!context) throw new Error('Missing component style context');

	return context as ComponentStyleContext;
}
